import { Add, Delete, ExpandMore } from '@mui/icons-material'
import {
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  TextField,
  FormControl,
  Box,
  IconButton,
  Button,
} from '@mui/material'
import { CenteringBox } from 'components/CenteringBox'
import { FileUpload } from 'components/FileUpload'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ILot } from 'types/lots'
import { DeepPartial, useFieldArray, useFormContext } from 'react-hook-form'
import { useDeleteTechnicalRequirementItemMutation } from 'services/lot/technical-requirements'

export const TechnicalProposal = ({ isDisabled }: { isDisabled: boolean }) => {
  const [deleteTechnicalRequirementItem] =
    useDeleteTechnicalRequirementItemMutation()

  const { setValue, watch, control, register } = useFormContext<DeepPartial<ILot>>()
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'technicalRequirement.technicalRequirementItems',
  })
  const technicalRequirementState = watch('technicalRequirement')
  const { t } = useTranslation()

  const handleAdd = useCallback(() => {
    append({
      criteria: '',
      eligibility: '',
      point: 0,
    })
  }, [append])

  return (
    <Accordion defaultExpanded sx={{ mt: 2 }}>
      <AccordionSummary
        expandIcon={<ExpandMore color="secondary" />}
        sx={(t) => ({
          backgroundColor: t.palette.primary.main,
          color: 'white',
          overflow: 'hidden',
        })}
      >
        <Typography sx={{ width: '50%', flexShrink: 0 }}>
          {t('technical-proposal')}
        </Typography>
        <Typography>
          {fields?.length || 0} {t('criterion')}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {fields?.map((item, index) => {
          return (
            <Box key={item.id} display="flex" gap={2} mb={2}>
              <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label={t('requirements')}
                  multiline
                  minRows={1}
                  variant="outlined"
                  {...register(
                    `technicalRequirement.technicalRequirementItems.${index}.criteria`
                  )}
                  disabled={isDisabled}
                />
              </FormControl>

              <FormControl sx={{ minWidth: 60 }} fullWidth margin="normal">
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label={t('weight')}
                  multiline
                  minRows={1}
                  variant="outlined"
                  type="number"
                  {...register(
                    `technicalRequirement.technicalRequirementItems.${index}.point`
                  )}
                  disabled={isDisabled}
                />
              </FormControl>

              <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label={t('notes-about-pricing')}
                  multiline
                  minRows={1}
                  variant="outlined"
                  {...register(
                    `technicalRequirement.technicalRequirementItems.${index}.eligibility`
                  )}
                  disabled={isDisabled}
                />
              </FormControl>

              <Box display="flex" alignItems="center" gap={0.5}>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => {
                    if (technicalRequirementState) {
                      deleteTechnicalRequirementItem({
                        technicalId: technicalRequirementState?.id as string,
                        itemId:
                          technicalRequirementState.technicalRequirementItems?.[
                            index
                          ]?.id,
                      })
                    }
                    remove(index)
                  }}
                  disabled={isDisabled}
                >
                  <Delete />
                </IconButton>
              </Box>
            </Box>
          )
        })}

        <CenteringBox mb={2}>
          <Button
            disabled={isDisabled}
            color="primary"
            variant="contained"
            style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
            onClick={handleAdd}
          >
            <Add />
          </Button>
        </CenteringBox>

        <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
          <TextField
            InputLabelProps={{ shrink: true }}
            fullWidth
            label={t('extra-info-short')}
            multiline
            minRows={4}
            variant="outlined"
            {...register('technicalRequirement.extraInfo')}
            disabled={isDisabled}
          />
        </FormControl>
        <FormControl sx={{ minWidth: 120 }} fullWidth margin="dense">
          <FileUpload
            text={t('extra-document')}
            handleFile={(file) => {
              setValue('technicalRequirement.extraFileId', file.id)
            }}
            onDelete={() => setValue('technicalRequirement.extraFileId', null)}
            fileId={technicalRequirementState?.extraFileId ?? undefined}
            isDisabled={isDisabled}
          />
        </FormControl>
      </AccordionDetails>
    </Accordion>
  )
}
